import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, RefreshCw, Save, Search, Radio, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import {
  listNetworkApplications, getNetworkApplication, setNetworkStatus,
  saveNetworkNotes, syncNetworkApplication, errText,
} from "../lib/api";

export const ROLE_META = {
  volunteer: {
    label: "Volunteers", singular: "Volunteer", base: "/control/volunteers",
    statuses: ["New Application", "Reviewing", "Follow-Up Needed", "Qualified", "Orientation Needed", "Ready", "Assigned", "Active", "Inactive", "Declined"],
    blurb: "Volunteer applications from Join The Network, with interests, skills and availability.",
    subtitle: (r) => [(r.availability || {}).frequency, (r.contact || {}).city].filter(Boolean).join(" · "),
    tags: (r) => (r.interests?.volunteer_interests || []).slice(0, 3),
  },
  "record-label": {
    label: "Labels & Industry", singular: "Record label", base: "/control/labels",
    statuses: ["New Application", "Reviewing", "Follow-Up Needed", "Qualified", "Intro Meeting", "Active Relationship", "On Hold", "Declined"],
    blurb: "Label, distributor, publisher, management and A&R enquiries.",
    subtitle: (r) => [(r.organization?.types || [])[0], (r.contact || {}).city].filter(Boolean).join(" · "),
    tags: (r) => (r.interest?.reaching_out_about || []).slice(0, 3),
  },
  partner: {
    label: "Partners", singular: "Partner", base: "/control/partner-orgs",
    statuses: ["New Application", "Reviewing", "Follow-Up Needed", "Qualified", "Intro Meeting", "Partnership Design", "Active Partner", "Strategic Partner", "On Hold", "Declined"],
    blurb: "Organization partner applications — schools, nonprofits, studios, showcases, platforms and companies.",
    subtitle: (r) => [(r.organization?.types || [])[0], (r.contact || {}).city].filter(Boolean).join(" · "),
    tags: (r) => (r.partnership?.interests || []).slice(0, 3),
  },
};

const flat = (v) => {
  if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) return "—";
  if (Array.isArray(v)) return v.map((x) => (typeof x === "object" ? Object.values(x).filter(Boolean).join(" · ") : x)).join(", ");
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return Object.entries(v).map(([k, x]) => `${k}: ${flat(x)}`).join(" | ");
  return String(v);
};

const title = (k) => k.replace(/_/g, " ").replace(/^./, (m) => m.toUpperCase());

export function NetworkList({ role }) {
  const meta = ROLE_META[role];
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => listNetworkApplications(role, { status, q: q || undefined }).then(setRows), 250);
    return () => clearTimeout(t);
  }, [role, status, q]);

  return (
    <div className="p-6 md:p-10">
      <h1 className="font-display text-3xl font-black text-zinc-50">{meta.label}</h1>
      <p className="mt-2 text-sm text-zinc-500">{meta.blurb}</p>

      <div className="mt-8 flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative md:max-w-sm md:flex-1">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
          <Input data-testid={`${role}-search`} value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, city, interest…"
            className="h-12 rounded-xl border-zinc-800 bg-zinc-900/70 pl-11 text-zinc-100 placeholder:text-zinc-600" />
        </div>
        <div className="flex flex-wrap gap-2">
          {["all", ...meta.statuses].map((s) => (
            <button key={s} data-testid={`${role}-filter-${s}`} onClick={() => setStatus(s)}
              className={`font-mono-tech rounded-full border px-3.5 py-2 text-[10px] transition-colors duration-200 ${
                status === s ? "border-brand-indigo bg-brand-indigo text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-10 border-t border-zinc-900" data-testid={`${role}-table`}>
        {rows.length === 0 && <p className="py-10 text-sm text-zinc-500">No {meta.singular.toLowerCase()} applications yet.</p>}
        {rows.map((r) => (
          <Link key={r.id} to={`${meta.base}/${r.id}`} data-testid={`${role}-row-${r.id}`}
            className="grid grid-cols-1 gap-3 border-b border-zinc-900 px-1 py-5 transition-colors duration-200 hover:bg-zinc-900/50 md:grid-cols-[1.6fr_1.4fr_1fr_auto] md:items-center">
            <div>
              <p className="font-display font-bold text-zinc-50">{r.display_name}</p>
              <p className="font-mono-tech text-[10px] text-zinc-500">{meta.subtitle(r)} · {r.created_at?.slice(0, 10)}</p>
            </div>
            <p className="text-sm text-zinc-400">{meta.tags(r).join(", ")}</p>
            <p className="font-mono-tech text-[10px] text-brand-indigo">{r.status}</p>
            <div className="flex flex-wrap gap-1.5">
              {r.minor_applicant && (
                <span className="font-mono-tech rounded-sm border border-brand-pink/40 bg-brand-pink/10 px-2 py-1 text-[9px] text-brand-pink">
                  <AlertTriangle className="inline h-2.5 w-2.5" /> MINOR
                </span>
              )}
              {r.notion?.synced && (
                <span className="font-mono-tech rounded-sm border border-green-500/40 bg-green-500/10 px-2 py-1 text-[9px] text-green-400">
                  <Radio className="inline h-2.5 w-2.5" /> SYNCED
                </span>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

const SKIP = ["id", "answers", "notion", "display_name", "status", "role", "application_type", "internal_notes", "created_at", "updated_at", "source"];

export function NetworkDetail({ role }) {
  const meta = ROLE_META[role];
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => getNetworkApplication(role, id).then((d) => { setDoc(d); setNotes(d.internal_notes || ""); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [role, id]);

  if (!doc) return <p className="font-mono-tech p-10 text-[11px] text-zinc-500">Loading…</p>;

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); await load(); toast.success(msg); }
    catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  const groups = Object.entries(doc).filter(([k]) => !SKIP.includes(k));

  return (
    <div className="p-6 md:p-10">
      <Link to={meta.base} data-testid="back-to-list" className="font-mono-tech flex items-center gap-2 text-[10px] text-zinc-500 hover:text-zinc-200">
        <ArrowLeft className="h-3 w-3" /> All {meta.label.toLowerCase()}
      </Link>

      <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-black text-zinc-50" data-testid="network-name">{doc.display_name}</h1>
          <p className="font-mono-tech mt-2 text-[10px] text-zinc-500">
            {meta.singular} · {doc.contact?.email} · submitted {doc.created_at?.slice(0, 10)}
          </p>
        </div>
        <Button data-testid="network-sync-btn" disabled={busy} onClick={() => act(() => syncNetworkApplication(role, id), "Synced to Notion")}
          className="h-11 rounded-full border border-zinc-800 bg-transparent px-5 text-sm text-zinc-200 hover:border-brand-indigo/60">
          <RefreshCw className="mr-2 h-4 w-4" /> Sync to Notion
        </Button>
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="network-status-controls">
        {meta.statuses.map((st) => (
          <button key={st} data-testid={`network-status-${st}`} disabled={busy}
            onClick={() => act(() => setNetworkStatus(role, id, st), `Status set to ${st}`)}
            className={`font-mono-tech rounded-full border px-3.5 py-2 text-[10px] transition-colors duration-200 ${
              doc.status === st ? "border-brand-indigo bg-brand-indigo text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"}`}>
            {st}
          </button>
        ))}
      </div>

      {doc.notion?.last_error && (
        <p className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300" data-testid="network-notion-error">
          Notion: {doc.notion.last_error}
        </p>
      )}

      <div className="md:grid md:grid-cols-[1fr_320px] md:gap-10">
        <div className="min-w-0">
          {groups.map(([key, value]) => (
            <section key={key} className="mt-10" data-testid={`block-${key}`}>
              <h2 className="font-mono-tech border-b border-zinc-800 pb-2 text-[11px] text-brand-indigo">{title(key)}</h2>
              <dl>
                {value && typeof value === "object" && !Array.isArray(value)
                  ? Object.entries(value).map(([k, v]) => (
                    <div key={k} className="grid gap-1 border-b border-zinc-900 py-3 sm:grid-cols-[38%_1fr] sm:gap-4">
                      <dt className="text-sm text-zinc-500">{title(k)}</dt>
                      <dd className="break-words text-sm text-zinc-200">{flat(v)}</dd>
                    </div>
                  ))
                  : (
                    <div className="grid gap-1 border-b border-zinc-900 py-3 sm:grid-cols-[38%_1fr] sm:gap-4">
                      <dt className="text-sm text-zinc-500">{title(key)}</dt>
                      <dd className="break-words text-sm text-zinc-200">{flat(value)}</dd>
                    </div>
                  )}
              </dl>
            </section>
          ))}
        </div>

        <aside className="mt-10 space-y-6">
          {doc.minor_applicant && (
            <div className="rounded-xl border border-brand-pink/40 bg-brand-pink/5 p-5" data-testid="minor-flag">
              <p className="font-mono-tech flex items-center gap-2 text-[10px] text-brand-pink">
                <AlertTriangle className="h-3.5 w-3.5" /> Minor applicant
              </p>
              <p className="mt-3 text-sm leading-relaxed text-zinc-300">
                Guardian consent and program review are required before any assignment, and before any minor-facing role.
              </p>
            </div>
          )}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="network-notes">
            <p className="font-mono-tech text-[10px] text-zinc-500">Internal notes</p>
            <Textarea data-testid="network-notes-input" rows={7} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="mt-3 rounded-xl border-zinc-800 bg-zinc-900/70 text-zinc-100" placeholder="Only the team sees this." />
            <Button data-testid="network-notes-save" disabled={busy} onClick={() => act(() => saveNetworkNotes(role, id, notes), "Notes saved")}
              className="mt-3 h-11 w-full rounded-full bg-brand-indigo text-sm font-semibold text-white hover:bg-brand-indigo-soft">
              <Save className="mr-2 h-4 w-4" /> Save notes
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
}
